import { useRouter } from '@hooks/useRouter';
import { useSessionStorage } from 'usehooks-ts';
import { getIsLocalhostOrNgrok } from 'vl-common/src/lib/saveAuthAndRedirect';
import { useCallback } from 'react';

export const DEEP_LINK_URL_KEY = 'deepLinkUrl';

export const EXCEPTIONAL_ROUTES = ['/policy', '/create-account'];

export const UNAUTHENTICATED_ROUTES = [
  '/login',
  '/wellbeinghub',
  '/forgot-password',
  '/patients/register',
  '/patients/legal',
  '/survey',
  '/floating-video',
  '/regulatory',
  '/events-test',
  '/accessibility',
  '/linklogin',
  '/axalogin',
  '/hbsbvt'
];

export const useDeepLink = () => {
  const router = useRouter();
  const [deepLinkUrl, setDeepLinkUrl, removeDeepLinkUrl] = useSessionStorage(DEEP_LINK_URL_KEY, '');

  const pushToLogin = useCallback(() => {
    const currentLocation = window.location.pathname + window.location.search;

    const isOnPublicPath = getIsOnPublicPath(window.location.pathname);

    if (!isOnPublicPath) {
      setDeepLinkUrl((value) => (value.trim() ? value : currentLocation));
    }

    // Ensure search params are passed to /login otherwise e.g. /patients/?autosso=AXAPATH won't work
    return router.push(maybeMakeAbsoluteUrl(`/login${window.location.search}`));
  }, [router, setDeepLinkUrl]);

  const pushToApp = useCallback(
    (
      /** If no deep link URL is saved, navigate to this path */
      fallbackPath: string
    ) => {
      if (EXCEPTIONAL_ROUTES.includes(fallbackPath)) {
        console.log('Navigating to fallback URL: ', fallbackPath);
        return router.push(fallbackPath);
      }

      if (deepLinkUrl) {
        removeDeepLinkUrl();
        console.log('Navigating to deep link URL: ', deepLinkUrl);
        return router.push(maybeMakeAbsoluteUrl(deepLinkUrl));
      }

      console.log('Navigating to fallback URL: ', fallbackPath);
      return router.push(fallbackPath);
    },
    [deepLinkUrl, router, removeDeepLinkUrl]
  );

  return {
    pushToLogin,
    pushToApp,
    deepLinkUrl
  };
};

// Unfortunately, it's sometimes necessary to perform these navigations outside of
// a React scope. These 'polyfills' replicate their behaviour for those cases.
export const pushToLoginNonReact = () => {
  const currentPath = window.location.pathname + window.location.search;

  const isOnPublicPath = getIsOnPublicPath(window.location.pathname);

  if (!isOnPublicPath) {
    sessionStorage.setItem(DEEP_LINK_URL_KEY, JSON.stringify(currentPath));
  }

  window.location.href = maybeMakeAbsoluteUrl(`/login${window.location.search}`);
};

export const pushToAppNonReact = (fallbackPath: string) => {
  if (EXCEPTIONAL_ROUTES.includes(fallbackPath)) {
    window.location.href = fallbackPath;
    return;
  }

  const deepLinkUrl = sessionStorage.getItem(DEEP_LINK_URL_KEY);

  if (deepLinkUrl) {
    sessionStorage.removeItem(DEEP_LINK_URL_KEY);
    window.location.href = maybeMakeAbsoluteUrl(deepLinkUrl);
  } else {
    window.location.href = maybeMakeAbsoluteUrl(fallbackPath);
  }
};

const maybeMakeAbsoluteUrl = (href: string) => {
  // Guarantee leading slash unless external http(s) link to stop appending to existing url
  if (!href.startsWith('/') && !href.startsWith('http')) {
    href = `/${href}`;
  }

  const isLocalhostOrNgrok = getIsLocalhostOrNgrok();
  if (isLocalhostOrNgrok) {
    return href;
  }

  const { hostname, pathname } = window.location;
  const pathSegments = pathname.split('/').filter(Boolean);

  let storyId = pathSegments.find(
    (segment) => segment.startsWith('vl') && (segment.endsWith('bug') || segment.endsWith('story'))
  );

  if (!storyId) {
    return href;
  }

  // If href is full URL, safely return it to avoid string corruption
  if (href.startsWith('http')) {
    // Clean up in case story ID needs removing from a full URL
    if (href.includes(storyId)) {
      return href.replace(`/${storyId}`, '');
    }
    return href;
  }

  // Clean join of href and storyId without double slashes
  storyId = storyId.startsWith('/') ? storyId : `/${storyId}`;

  // Final join of all parts
  return `https://${hostname}${storyId}${href}`;
};

export const getRelativePath = (pathname: string) => {
  const firstSegment = pathname.split('/')[1];

  const isStoryBranch =
    firstSegment.startsWith('vl') && (firstSegment.endsWith('story') || firstSegment.endsWith('bug'));

  if (isStoryBranch) {
    return `/${pathname.split('/').slice(2).join('/')}`;
  }

  return pathname;
};

const getIsOnPublicPath = (path: string) => {
  const relativePathname = getRelativePath(path);

  return UNAUTHENTICATED_ROUTES.some((url) => relativePathname.startsWith(url)) || relativePathname === '/';
};
